import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const VisualComparisonRight: React.FC = React.memo(() => {
  return (
    <section 
      className="relative bg-white p-4 rounded-xl shadow-md border border-blue-100"
      aria-labelledby="strive-process-title"
      role="region"
    >
      {/* Title Section */}
      <header className="text-center mb-4">
        <h2 
          id="strive-process-title"
          className="text-4xl font-bold text-gray-900"
        >
          Know what to focus on before anyone asks
        </h2>
      </header>
      
      {/* Visual Section - Stacked Software Interfaces */}
      <div 
        className="relative mb-4"
        aria-label="Integrated software interfaces visualization"
        role="img"
      >
        <div className="w-full h-[400px] bg-white rounded-lg overflow-hidden relative shadow-inner border border-blue-100">
          {/* CSS for animations and card transforms */}
          <style>
            {`
              .card {
                transform-origin: center center;
                filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.15));
                pointer-events: none;
                user-select: none;
              }
            `}
          </style>
          
          {/* Stacked Card Layout */}
          <svg 
            viewBox="0 0 1200 800" 
            className="absolute inset-0 w-full h-full select-none"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Integration visualization"
            role="presentation"
          >
            {/* Background */}
            <rect x="0" y="0" width="1200" height="800" fill="white" />
            
            {/* Email Card - Bottom */}
            <g className="card" style={{ transform: 'rotate(-20deg) translate(200px, 280px)' }} aria-label="Email integration">
              <rect x="0" y="0" width="500" height="300" rx="12" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="2" />
              <rect x="0" y="0" width="500" height="60" rx="12" fill="#D44638" />
              <text x="30" y="38" fill="white" fontSize="24" fontWeight="600">Email</text>
              <rect x="30" y="90" width="440" height="30" rx="6" fill="#E5E7EB" />
              <rect x="30" y="140" width="400" height="30" rx="6" fill="#E5E7EB" />
              <rect x="30" y="190" width="360" height="30" rx="6" fill="#E5E7EB" />
            </g>

            {/* CRM Card */}
            <g className="card" style={{ transform: 'rotate(-12deg) translate(300px, 180px)' }} aria-label="CRM integration">
              <rect x="0" y="0" width="500" height="300" rx="12" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="2" />
              <rect x="0" y="0" width="500" height="60" rx="12" fill="#00A1E0" />
              <text x="30" y="38" fill="white" fontSize="24" fontWeight="600">CRM</text>
              <line x1="30" y1="110" x2="470" y2="110" stroke="#D1D5DB" strokeWidth="2" />
              <line x1="30" y1="160" x2="470" y2="160" stroke="#D1D5DB" strokeWidth="2" />
              <line x1="30" y1="210" x2="470" y2="210" stroke="#D1D5DB" strokeWidth="2" />
            </g>

            {/* Zoom Card */}
            <g className="card" style={{ transform: 'rotate(-4deg) translate(400px, 80px)' }} aria-label="Zoom integration">
              <rect x="0" y="0" width="500" height="300" rx="12" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="2" />
              <rect x="0" y="0" width="500" height="60" rx="12" fill="#2D8CFF" />
              <text x="30" y="38" fill="white" fontSize="24" fontWeight="600">Zoom</text>
              <rect x="30" y="90" width="440" height="180" rx="6" fill="#1A6AD2" />
            </g>

            {/* Slack Card - Top */}
            <g className="card" style={{ transform: 'rotate(4deg) translate(500px, -20px)' }} aria-label="Slack integration">
              <rect x="0" y="0" width="500" height="300" rx="12" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="2" />
              <rect x="0" y="0" width="500" height="60" rx="12" fill="#4A154B" />
              <text x="30" y="38" fill="white" fontSize="24" fontWeight="600">Slack</text>
              <rect x="30" y="90" width="440" height="30" rx="6" fill="#D1D5DB" />
              <rect x="30" y="140" width="400" height="30" rx="6" fill="#D1D5DB" />
              <rect x="30" y="190" width="360" height="30" rx="6" fill="#D1D5DB" />
            </g>
          </svg>
        </div>
      </div>
      
      {/* Description Section */}
      <div className="text-center mb-6">
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Upload what you already have and Strive transforms it into clarity, priorities, and progress.
        </p>
      </div>
      
      {/* Benefits Section */}
      <div 
        className="grid grid-cols-1 gap-4"
        role="list"
        aria-label="Process improvements"
      >
        <div className="flex items-start gap-3" role="listitem">
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-green-100">
            <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Insights from Your Existing Documents</h3>
            <p className="text-sm text-gray-600">Upload documents, product briefs, notes, and more. Strive automatically extracts valuable insights without manual setup.</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3" role="listitem">
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-green-100">
            <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Seamless Data Integration</h3>
            <p className="text-sm text-gray-600">Import CSVs from Jira, Aha, or Gong. Strive lets you merge, shape, and explore your product data in one place.</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3" role="listitem">
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-green-100">
            <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">AI Powered Priority Analysis</h3>
            <p className="text-sm text-gray-600">Our workflows analyze your inputs and surface clear priorities using real context and data.</p>
          </div>
        </div>
      </div>
    </section>
  );
});

VisualComparisonRight.displayName = 'VisualComparisonRight';

export default VisualComparisonRight; 